import BluebirdPromise from 'bluebird';
const { Promise } = require('bluebird');
import { Time } from 'app/scripts/lib/time';
import { QuickLoad } from '@trello/quickload';
import type { Callback, QuickloadError } from '@trello/quickload';
import { Analytics } from '@trello/atlassian-analytics';
import { apiErrorFromResponse } from 'app/scripts/network/apiErrorFromResponse';
import { sendNetworkErrorEvent } from '@trello/error-reporting';
import { assert } from '@trello/error-handling';
import { waitFor } from './waitFor';

type Response = JSON | JSON[] | null;
const preloadUncalledSentinel = {};

const quickload = function (
  url: string,
  operationName: string,
  traceId?: string | null,
): BluebirdPromise<[Response, XMLHttpRequest]> {
  let preload = preloadUncalledSentinel;
  return Promise.fromNode(
    (next: Callback) =>
      // @ts-expect-error
      (preload = QuickLoad.load(
        url,
        next,
        operationName,
        'model-loader',
        traceId,
        traceId ? Analytics.getTaskForTraceId(traceId) : null,
      )),
  )
    .catch(function (args: QuickloadError) {
      const [statusCode, responseText] = args;
      const apiError = apiErrorFromResponse(statusCode, responseText);
      sendNetworkErrorEvent({
        status: statusCode,
        response: apiError.toString(),
        url,
        operationName,
      });
      return Promise.reject(apiError);
    })
    .tap(() =>
      assert(
        preload !== preloadUncalledSentinel,
        "Promises resolved synchronously; didn't get preload information from QuickLoad",
      ),
    )
    .spread((data: Response, xhr: XMLHttpRequest) => [data, xhr, preload]);
};

const updateServerTime = function (xhr: XMLHttpRequest) {
  let serverTime;
  // eslint-disable-next-line eqeqeq
  if ((serverTime = xhr.getResponseHeader('X-Server-Time')) != null) {
    Time.updateServerTime(parseInt(serverTime, 10));
  }
};

export const loadApiDataFromUrl = (
  url: string,
  {
    operationName,
    modelType,
    traceId,
    isHeaderLoad = false,
  }: {
    operationName?: string;
    modelType: string;
    isHeaderLoad?: boolean | null;
    traceId?: string | null;
  },
) => {
  return quickload(url, operationName ?? `load:${modelType}`, traceId)
    .spread(function (data: Response, xhr: XMLHttpRequest) {
      updateServerTime(xhr);

      return [data, xhr];
    })
    .tap(function () {
      // We skip this logic when we're running GraphiQL because the header is never loaded in that context.
      if (isHeaderLoad) {
        return;
      } else {
        return new BluebirdPromise(function (resolve) {
          return waitFor('headerData', resolve);
        });
      }
    });
};
